import { ref } from 'vue'
import { useResizeHandler } from '@shared/utils/composables/resize-handler'

export function useAbsoluteDragNDrop({
  parent,
  child,
  paddingX,
  paddingY,
}: {
  parent?: HTMLElement
  child?: HTMLElement
  paddingX: number
  paddingY: number
}) {
  const { mdAndUp } = useResizeHandler()
  const isDragging = ref(false)
  const startX = ref(0)
  const startY = ref(0)

  const onPointerDownHandler = (event: MouseEvent) => {
    if (!parent || !child) {
      return
    }
    isDragging.value = true
    startX.value = event.clientX - child.offsetLeft
    startY.value = event.clientY - child.offsetTop
    document.addEventListener('pointermove', onPointerMoveHandler)
    document.addEventListener('pointerup', onPointerUpHandler)
  }

  const onTouchStartHandler = (event: TouchEvent) => {
    if (!parent || !child) {
      return
    }
    isDragging.value = true
    startX.value = event.touches[0].clientX - child.offsetLeft
    startY.value = event.touches[0].clientY - child.offsetTop
    document.addEventListener('touchmove', onTouchMoveHandler)
    document.addEventListener('touchend', onTouchEndHandler)
  }

  const onTouchMoveHandler = (event: TouchEvent) => {
    if (!parent || !child) {
      return
    }
    if (isDragging.value) {
      child.style.setProperty('right', 'unset', 'important')
      child.style.setProperty('bottom', 'unset')
      let x = event.touches[0].clientX - startX.value
      let y = event.touches[0].clientY - startY.value
      x = Math.max(x, paddingX)
      y = Math.max(y, paddingY)
      const maxX = parent.clientWidth - child.offsetWidth - paddingX
      const maxY = parent.clientHeight - child.offsetHeight - paddingY
      x = Math.min(x, maxX)
      y = Math.min(y, maxY)
      child.style.setProperty('left', `${x}px`)
      child.style.setProperty('top', `${y}px`)
    }
  }

  const onPointerMoveHandler = (event: MouseEvent) => {
    if (!parent || !child) {
      return
    }
    if (isDragging.value) {
      child.style.setProperty('right', 'unset', 'important')
      child.style.setProperty('bottom', 'unset')
      let x = event.clientX - startX.value
      let y = event.clientY - startY.value
      x = Math.max(x, paddingX)
      y = Math.max(y, paddingY)
      const maxX = parent.clientWidth - child.offsetWidth - paddingX
      const maxY = parent.clientHeight - child.offsetHeight - paddingY
      x = Math.min(x, maxX)
      y = Math.min(y, maxY)
      child.style.setProperty('left', `${x}px`)
      child.style.setProperty('top', `${y}px`)
    }
  }

  const onPointerUpHandler = () => {
    if (!parent || !child) {
      return
    }
    isDragging.value = false
    const containerRect = parent.getBoundingClientRect()
    const blockRect = child.getBoundingClientRect()
    const blockX = blockRect.left - containerRect.left
    const blockY = blockRect.top - containerRect.top
    let cornerX =
      Math.round(blockX / (parent.clientWidth - 2 * paddingX)) * (parent.clientWidth - 2 * paddingX) + paddingX
    let cornerY = Math.round(blockY / (parent.clientHeight - paddingY)) * (parent.clientHeight - paddingY) + paddingY
    cornerX = Math.max(cornerX, paddingX)
    cornerY = Math.max(cornerY, paddingY)
    const maxX = parent.clientWidth - child.offsetWidth - paddingX
    const maxY = parent.clientHeight - child.offsetHeight - paddingY
    cornerX = Math.min(cornerX, maxX)
    cornerY = Math.min(cornerY, maxY)
    child.style.setProperty('left', `${cornerX}px`)
    child.style.setProperty('top', `${cornerY}px`)
    document.removeEventListener('pointermove', onPointerMoveHandler)
    document.removeEventListener('pointerup', onPointerUpHandler)
  }

  const onTouchEndHandler = () => {
    if (!parent || !child) {
      return
    }
    isDragging.value = false
    const containerRect = parent.getBoundingClientRect()
    const blockRect = child.getBoundingClientRect()
    const blockX = blockRect.left - containerRect.left
    const blockY = blockRect.top - containerRect.top
    let cornerX =
      Math.round(blockX / (parent.clientWidth - 2 * paddingX)) * (parent.clientWidth - 2 * paddingX) + paddingX
    let cornerY = Math.round(blockY / (parent.clientHeight - paddingY)) * (parent.clientHeight - paddingY) + paddingY
    cornerX = Math.max(cornerX, paddingX)
    cornerY = Math.max(cornerY, paddingY)
    const maxX = parent.clientWidth - child.offsetWidth - paddingX
    const maxY = parent.clientHeight - child.offsetHeight - paddingY
    cornerX = Math.min(cornerX, maxX)
    cornerY = Math.min(cornerY, maxY)
    child.style.setProperty('top', `${cornerY}px`)
    child.style.setProperty('left', `${cornerX}px`)
    document.removeEventListener('touchmove', onTouchMoveHandler)
    document.removeEventListener('touchend', onTouchEndHandler)
  }

  const onChangeScreenHandler = () => {
    if (!mdAndUp) {
      return
    }
    normalizePosition()
  }

  const normalizePosition = () => {
    if (!parent || !child) {
      return
    }
    child.style.setProperty('left', 'unset')
    child.style.setProperty('top', 'unset')
    child.style.setProperty('bottom', `${paddingY}px`)
    child.style.setProperty('right', `${paddingX}px`, 'important')
  }

  return {
    onChangeScreenHandler,
    normalizePosition,
    onPointerUpHandler,
    onPointerMoveHandler,
    onPointerDownHandler,
    onTouchStartHandler,
  }
}
